import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IS_NATIVE } from 'gcs-common/constants/deviceConstants';
import type { PusherConnectionState } from 'gcs-common/pusher/pusher-constants';
import { usePusher } from 'gcs-common/pusher/pusher-context';
import { updateConnectionState } from 'gcs-common/slices/chatConnection/chatConnectionSlice';
import { useDispatch } from 'react-redux';
import type { RequestBody } from '../../../../../types/typeHelper';
import { capacitorSecureStorageService } from '../../../../helper/capacitor-secure-storage-service';
import { openErrorDialog } from '../../../../slices/errorDialogSlice/errorDialogSlice';
import { useApiClient } from '../../client';
import { CurrentUserQueryKeys } from '../current-user/current-user-api';

export function useRequestLoginMutation() {
  const client = useApiClient();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { pusher } = usePusher();

  return useMutation({
    mutationFn: async ({ body }: { body: RequestBody<'/login', 'post'> }) => {
      return client.POST('/login', { body });
    },
    onSuccess: async ({ data }) => {
      await queryClient.invalidateQueries({ queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER] });
      dispatch(updateConnectionState({ connectionState: pusher.connection.state as PusherConnectionState }));
      if (IS_NATIVE && data?.refresh_token) {
        capacitorSecureStorageService.setSessionRefreshToken(data.refresh_token);
      }
    },
    onError: async (error) => {
      dispatch(openErrorDialog({ dialogErrorMsg: error.message }));
    },
  });
}

export function useRefreshSessionMutation() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { pusher } = usePusher();

  return useMutation({
    mutationFn: async ({ body }: { body: RequestBody<'/refresh_session', 'post'> }) => {
      return client.POST('/refresh_session', { body });
    },
    onSuccess: async ({ data }) => {
      await queryClient.invalidateQueries({ queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER] });
      if (IS_NATIVE && data?.renewed_refresh_token) {
        await capacitorSecureStorageService.setSessionRefreshToken(data.renewed_refresh_token);
      }
      dispatch(updateConnectionState({ connectionState: pusher.connection.state as PusherConnectionState }));
    },
  });
}
